import { useState, useCallback, useRef, useEffect } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';

/* ──────────────────────────────────────────────────────────────────────
 * VoiceInput — complete rebuild (clean state-machine, mobile-first)
 *
 * States: idle → requesting → listening → processing → idle
 *                                                    → error → idle
 *         idle → unsupported (terminal until remount)
 *
 * Only ONE state is ever active. No overlapping toasts/UI.
 * ────────────────────────────────────────────────────────────────────── */

type VoiceState = 'idle' | 'requesting' | 'listening' | 'processing' | 'error' | 'unsupported';

interface VoiceInputProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
  className?: string;
  size?: 'default' | 'sm' | 'lg' | 'icon';
  showWaveform?: boolean;
}

// Minimum listen duration (ms) before we treat empty transcript as "no speech"
const MIN_LISTEN_MS = 3000;
// Maximum silent retries when session ends too fast
const MAX_SILENT_RETRIES = 1;

function getSpeechCtor(): any {
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
}

function isMobileDevice(): boolean {
  const ua = navigator.userAgent || '';
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isPlatformSupported(): boolean {
  if (isMobileDevice()) return false;
  const ua = navigator.userAgent || '';
  const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|Edg/i.test(ua);
  if (isSafari) return false;
  return !!getSpeechCtor();
}

export function VoiceInput({
  onTranscript,
  disabled = false,
  className,
  size = 'icon',
}: VoiceInputProps) {
  const { language } = useLanguage();
  const [state, setState] = useState<VoiceState>('idle');

  // Refs — single source of truth for the active session
  const recognitionRef = useRef<any>(null);
  const transcriptRef = useRef('');
  const startTimeRef = useRef(0);
  const retryCountRef = useRef(0);
  const stoppedByUserRef = useRef(false);
  // Guard: prevent any handler from running after we've transitioned away
  const sessionIdRef = useRef(0);

  /* ── Hard cleanup (no side-effects, no toasts) ───────────────────── */
  const teardown = useCallback(() => {
    const r = recognitionRef.current;
    if (r) {
      // Detach all handlers before stopping to avoid ghost events
      r.onresult = null;
      r.onerror = null;
      r.onend = null;
      r.onstart = null;
      try { r.abort(); } catch { /* noop */ }
      recognitionRef.current = null;
    }
    transcriptRef.current = '';
    startTimeRef.current = 0;
    stoppedByUserRef.current = false;
  }, []);

  // Cleanup on unmount
  useEffect(() => () => { sessionIdRef.current += 1; teardown(); }, [teardown]);

  /* ── Build & wire a fresh SpeechRecognition instance ─────────────── */
  const createSession = useCallback((sessionId: number): any | null => {
    const Ctor = getSpeechCtor();
    if (!Ctor) return null;

    const r = new Ctor();
    r.continuous = true;
    r.interimResults = true;
    r.maxAlternatives = 1;
    r.lang = language === 'es' ? 'es-ES'
           : language === 'fr' ? 'fr-FR'
           : language === 'pt' ? 'pt-BR'
           : language === 'ar' ? 'ar-SA'
           : language === 'ja' ? 'ja-JP'
           : language === 'ko' ? 'ko-KR'
           : language === 'id' ? 'id-ID'
           : 'en-US';

    /* —— onresult —— */
    r.onresult = (e: SpeechRecognitionEvent) => {
      if (sessionIdRef.current !== sessionId) return;
      let finals = '';
      for (let i = 0; i < e.results.length; i++) {
        if (e.results[i]?.isFinal) {
          const t = e.results[i][0]?.transcript?.trim();
          if (t) finals += ' ' + t;
        }
      }
      if (finals.trim()) {
        transcriptRef.current = finals.trim();
        console.log('[STT] transcript_received', { len: transcriptRef.current.length });
      }
    };

    /* —— onerror —— */
    r.onerror = (e: SpeechRecognitionErrorEvent) => {
      if (sessionIdRef.current !== sessionId) return;
      const code = e.error;
      console.warn('[STT] recognition_error', code);

      // 'aborted' = we called .stop()/.abort() — expected, let onend handle it
      if (code === 'aborted') return;

      // 'no-speech' = browser detected silence — let onend handle it
      if (code === 'no-speech') return;

      // 'not-allowed' after we were already listening = spurious Android event
      if (code === 'not-allowed' && stoppedByUserRef.current) return;

      // True permission denial
      if (code === 'not-allowed') {
        sessionIdRef.current += 1;
        teardown();
        setState('idle');
        toast.error('Microphone access is needed for voice typing. Please enable permissions.');
        return;
      }

      // network / service-not-allowed / etc — non-fatal, onend will finalize
      console.log('[STT] non-fatal error, waiting for onend');
    };

    /* —— onend —— */
    r.onend = () => {
      if (sessionIdRef.current !== sessionId) return;
      const transcript = transcriptRef.current.trim();
      const elapsed = Date.now() - startTimeRef.current;
      console.log('[STT] recognition_ended', { elapsed, transcriptLen: transcript.length, retries: retryCountRef.current, stoppedByUser: stoppedByUserRef.current });

      // Success — deliver transcript
      if (transcript) {
        sessionIdRef.current += 1;
        teardown();
        setState('idle');
        onTranscript(transcript);
        retryCountRef.current = 0;
        toast.success('Transcription complete!');
        return;
      }

      // User explicitly stopped but nothing captured
      if (stoppedByUserRef.current) {
        sessionIdRef.current += 1;
        teardown();
        setState('idle');
        retryCountRef.current = 0;
        toast('No speech detected. Please try again.', { duration: 3000 });
        return;
      }

      // Ended too quickly on its own — silent auto-retry
      if (elapsed < MIN_LISTEN_MS && retryCountRef.current < MAX_SILENT_RETRIES) {
        console.log('[STT] ended too quickly, silent retry');
        retryCountRef.current += 1;
        recognitionRef.current = null;
        const fresh = createSession(sessionId);
        if (fresh) {
          recognitionRef.current = fresh;
          transcriptRef.current = '';
          startTimeRef.current = Date.now();
          try {
            fresh.start();
            console.log('[STT] retry_started');
            return;
          } catch (err) {
            console.warn('[STT] retry_start_failed', err);
          }
        }
      }

      // Genuine no-speech after adequate time
      sessionIdRef.current += 1;
      teardown();
      setState('idle');
      retryCountRef.current = 0;
      toast('No speech detected. Please try again.', { duration: 3000 });
    };

    return r;
  }, [language, onTranscript, teardown]);

  /* ── Start flow ──────────────────────────────────────────────────── */
  const start = useCallback(() => {
    console.log('[STT] mic_tapped');

    // Platform check
    if (!isPlatformSupported()) {
      console.log('[STT] platform_unsupported');
      setState('unsupported');
      toast("Voice typing isn't available on this device yet. You can type your message or record a video instead.", { duration: 5000 });
      // Reset after toast so button is usable again (user may switch browsers)
      setTimeout(() => setState('idle'), 5000);
      return;
    }

    setState('requesting');

    // Bump session to invalidate any lingering handlers
    sessionIdRef.current += 1;
    teardown();

    const sessionId = sessionIdRef.current;
    const recognition = createSession(sessionId);
    if (!recognition) {
      setState('idle');
      toast.error('Voice typing is not available.', { duration: 3000 });
      return;
    }

    recognitionRef.current = recognition;
    transcriptRef.current = '';
    retryCountRef.current = 0;
    stoppedByUserRef.current = false;
    startTimeRef.current = Date.now();

    try {
      recognition.start();
      console.log('[STT] recognition_started');
      setState('listening');
      toast.info('🎤 Listening… Tap again to stop', { duration: 2000 });
    } catch (err: any) {
      console.error('[STT] recognition_start_failed', err?.name, err?.message);
      teardown();
      setState('idle');
      toast.error("We couldn't start voice input. Please try again.");
    }
  }, [createSession, teardown]);

  /* ── Stop flow ───────────────────────────────────────────────────── */
  const stop = useCallback(() => {
    console.log('[STT] stop_tapped');
    stoppedByUserRef.current = true;
    setState('processing');
    const r = recognitionRef.current;
    if (r) {
      try { r.stop(); } catch { /* onend will still fire from abort */ }
    } else {
      setState('idle');
    }
  }, []);

  /* ── Toggle ──────────────────────────────────────────────────────── */
  const toggle = useCallback(() => {
    if (state === 'listening') stop();
    else if (state === 'idle' || state === 'error') start();
  }, [state, start, stop]);

  /* ── Render ──────────────────────────────────────────────────────── */
  if (state === 'idle' || state === 'unsupported' || state === 'error') {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={toggle}
        disabled={disabled || state === 'unsupported'}
        className={cn('text-muted-foreground hover:text-foreground gap-1.5 h-7 px-2', className)}
        title="Speak instead of typing"
      >
        <Mic className="h-3.5 w-3.5" />
        <span className="text-xs mx-0 px-0 py-0 pr-[50px]">Speak to type</span>
      </Button>
    );
  }

  if (state === 'requesting') {
    return (
      <div className={cn('flex items-center gap-2 w-full justify-end', className)}>
        <Button type="button" variant="default" size="sm" disabled className="gap-2 px-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Starting…</span>
        </Button>
      </div>
    );
  }

  if (state === 'processing') {
    return (
      <div className={cn('flex items-center gap-2 w-full justify-end', className)}>
        <Button type="button" variant="default" size="sm" disabled className="gap-2 px-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Processing…</span>
        </Button>
      </div>
    );
  }

  // state === 'listening'
  return (
    <div className={cn('flex items-center gap-2 w-full justify-end', className)}>
      <Button
        type="button"
        variant="default"
        size="sm"
        onClick={toggle}
        className="bg-destructive hover:bg-destructive/90 text-destructive-foreground gap-2 px-4 transition-all duration-200"
        title="Tap to stop"
      >
        <MicOff className="h-4 w-4" />
        <span className="text-sm">Tap to stop</span>
      </Button>
    </div>
  );
}
